import { repo } from '@/db/repo'
import { ApiError, type SongMedia, type UploadLimits } from '@/types'
import { sortBy, uid } from '@/lib/utils'
import { required, requireBody, type Route } from '@/services/api/router'

/**
 * Offline equivalent of the PHP media endpoints.
 *
 * The audio lives in IndexedDB as a Blob. `blob:` URLs die with the document,
 * so they are minted on read and memoised for the life of the tab rather than
 * stored alongside the metadata.
 */
const objectUrls = new Map<string, string>()

async function urlFor(mediaId: string): Promise<string> {
  const cached = objectUrls.get(mediaId)
  if (cached) return cached
  const record = await repo.get('mediaBlobs', mediaId)
  if (!record) return ''
  const url = URL.createObjectURL(record.blob)
  objectUrls.set(mediaId, url)
  return url
}

async function hydrate(media: SongMedia): Promise<SongMedia> {
  // A linked video already carries its own playable URL.
  if (media.provider === 'link') return media
  return { ...media, url: await urlFor(media.id) }
}

/** Mock mode is bounded by IndexedDB, not by PHP — advertise a sane ceiling. */
const MOCK_LIMITS: UploadLimits = {
  maxFileBytes: 64 * 1024 * 1024,
  maxPostBytes: 64 * 1024 * 1024,
  allowedExtensions: ['mp3', 'wav', 'm4a', 'aac', 'ogg', 'flac'],
  allowedMimeTypes: ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/aac', 'audio/ogg', 'audio/flac'],
  storageUsedBytes: 0,
}

export const mediaRoutes: Route[] = [
  {
    method: 'GET',
    pattern: '/media/limits',
    visibility: 'admin',
    description: 'Upload ceilings and accepted formats, so the UI can reject files early.',
    handler: async () => {
      const all = await repo.all('songMedia')
      return {
        ...MOCK_LIMITS,
        storageUsedBytes: all.reduce((sum, m) => sum + m.sizeBytes, 0),
      }
    },
  },
  {
    method: 'GET',
    pattern: '/songs/:songId/media',
    visibility: 'admin',
    description: 'Audio attached to a song, in rehearsal order.',
    handler: async (ctx) => {
      const media = await repo.byIndex('songMedia', 'by-song', ctx.params.songId)
      return Promise.all(sortBy(media, (m) => m.sortOrder).map(hydrate))
    },
  },
  {
    method: 'PATCH',
    pattern: '/media/:mediaId',
    visibility: 'admin',
    description: 'Renames a track or changes its kind/arrangement.',
    handler: async (ctx) => {
      const body = requireBody<Partial<SongMedia>>(ctx.body)
      const saved = required(
        await repo.patch('songMedia', ctx.params.mediaId, body),
        'Media',
        ctx.params.mediaId,
      )
      return hydrate(saved)
    },
  },
  {
    method: 'DELETE',
    pattern: '/media/:mediaId',
    visibility: 'admin',
    description: 'Removes a track and its stored audio.',
    handler: async (ctx) => {
      const { mediaId } = ctx.params
      const existing = objectUrls.get(mediaId)
      if (existing) {
        URL.revokeObjectURL(existing)
        objectUrls.delete(mediaId)
      }
      await repo.remove('songMedia', mediaId)
      await repo.remove('mediaBlobs', mediaId)
      return { id: mediaId, deleted: true }
    },
  },
]

/** Attaches a practice video by URL. Mirrors POST /songs/:id/links. */
export async function storeLinkLocally(input: {
  songId: string
  url: string
  label?: string
  arrangementId?: string | null
}): Promise<SongMedia> {
  const song = await repo.get('songs', input.songId)
  if (!song) throw new ApiError(404, 'not_found', `Song "${input.songId}" was not found`)
  if (!/^https?:\/\//i.test(input.url.trim())) {
    throw new ApiError(400, 'invalid_url', 'That does not look like a web address.')
  }

  const existing = await repo.byIndex('songMedia', 'by-song', input.songId)
  const now = new Date().toISOString()
  const media: SongMedia = {
    id: uid('media'),
    songId: input.songId,
    arrangementId: input.arrangementId ?? null,
    kind: 'youtube',
    label: input.label?.trim() || 'Practice video',
    fileName: '',
    originalName: input.url,
    mimeType: 'text/html',
    sizeBytes: 0,
    durationSeconds: null,
    sortOrder: existing.reduce((max, m) => Math.max(max, m.sortOrder), 0) + 1,
    provider: 'link',
    url: input.url.trim(),
    createdAt: now,
    updatedAt: now,
  }
  await repo.put('songMedia', media)
  return media
}

export interface UploadInput {
  songId: string
  file: File
  label?: string
  kind?: SongMedia['kind']
  arrangementId?: string | null
  durationSeconds?: number | null
}

/** Stores an upload locally. Mirrors what POST /songs/:id/media does server-side. */
export async function storeMediaLocally(input: UploadInput): Promise<SongMedia> {
  const song = await repo.get('songs', input.songId)
  if (!song) throw new ApiError(404, 'not_found', `Song "${input.songId}" was not found`)

  const extension = input.file.name.split('.').pop()?.toLowerCase() ?? ''
  if (!MOCK_LIMITS.allowedExtensions.includes(extension)) {
    throw new ApiError(
      415,
      'unsupported_type',
      `Only ${MOCK_LIMITS.allowedExtensions.join(', ')} files can be uploaded.`,
    )
  }

  const existing = await repo.byIndex('songMedia', 'by-song', input.songId)
  const id = uid('media')
  const now = new Date().toISOString()

  const media: SongMedia = {
    id,
    songId: input.songId,
    arrangementId: input.arrangementId ?? null,
    kind: input.kind ?? 'reference',
    label: input.label?.trim() || input.file.name.replace(/\.[^.]+$/, ''),
    fileName: `${id}.${extension}`,
    originalName: input.file.name,
    mimeType: input.file.type || 'audio/mpeg',
    sizeBytes: input.file.size,
    durationSeconds: input.durationSeconds ?? null,
    sortOrder: existing.reduce((max, m) => Math.max(max, m.sortOrder), 0) + 1,
    provider: 'file',
    url: '',
    createdAt: now,
    updatedAt: now,
  }

  await repo.put('mediaBlobs', { id, blob: input.file })
  await repo.put('songMedia', media)
  return hydrate(media)
}
